import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';

import {Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    CountriesService,
    CountryInterface,
    CountryModel,
    HttpStatusCodesEnum,
    IndividualFilterModel,
    IndividualReportInterface,
    IndividualReportModel,
    LookupModel,
    PortalModel,
    ReportsService,
    ServerResponseData,
    TenantsService,
    TradingPlatformInfoInterface,
    TradingPlatformInfoModel,
    TradingPlatformService
} from '@spa/api';

import {EnumsHelper, FilterControl, Helper} from '../../../shared/classes';
import {Pagination, ReportTableConfigInterface} from '../../../shared/components';

import {ClientEntitiesResolverErrorStatesEnum} from '../enums';
import {ClientEntitiesReportResolverDataInterface} from '../interfaces';
import {PortalInterface} from '@spa/api/lib/interfaces';
import {CacheCollectionService} from 'appShared/services';
import {ComponentStorageKeyProvider} from 'appShared/providers';
import {variables} from "appAssets/tenants/variables";

export abstract class ClientEntitiesReportResolver
    implements Resolve<ApiResponse<ApiError[] | ClientEntitiesReportResolverDataInterface>> {

    protected componentStorageKeyProvider: ComponentStorageKeyProvider;

    protected constructor(
        protected tenantsService: TenantsService,
        protected countriesService: CountriesService,
        protected reportsService: ReportsService,
        protected tradingPlatformService: TradingPlatformService,
        protected cacheCollectionService: CacheCollectionService,
        protected storageId: string,
    ) {
        this.componentStorageKeyProvider = new ComponentStorageKeyProvider(storageId);
    }

    private countriesMap(
        response: ApiResponse<ApiError[] | CountryInterface[]>
    ): Observable<ApiResponse<ApiError[] | ClientEntitiesReportResolverDataInterface>> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponseObservable(
                response,
                ClientEntitiesResolverErrorStatesEnum.GetCountriesFailed
            );
        }

        if (Helper.errorsHelper().checkForInvalidRespondedArray<CountryInterface>(response)) {
            const errorResponse = Helper.errorsHelper().createResponseForInvalidData(
                ClientEntitiesResolverErrorStatesEnum.GetCountriesInvalidData
            );

            return of(errorResponse);
        }

        let countries = [];

        if (response.data !== null) {
            countries = (response.data as CountryInterface[]).map((item: CountryInterface) => new CountryModel(item));

            countries = Helper.sortingHelper().sortCountriesAlphabetically(countries);
        }

        return this.tenantsService.getPortals().pipe(
            switchMap(this.mapPortals.bind(this, countries))
        );
    }

    private mapPortals(
        countries: CountryModel[],
        response: ServerResponseData<PortalInterface[]>
    ): Observable<ApiResponse<ApiError[] | ClientEntitiesReportResolverDataInterface>> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponseObservable(
                response,
                ClientEntitiesResolverErrorStatesEnum.GetCountriesFailed
            );
        }

        if (Helper.errorsHelper().checkForInvalidRespondedArray<PortalInterface>(response)) {
            const errorResponse = Helper.errorsHelper().createResponseForInvalidData(
                ClientEntitiesResolverErrorStatesEnum.GetCountriesInvalidData
            );

            return of(errorResponse);
        }

        let portals = [];

        if (response.data !== null) {
            portals = (response.data as PortalInterface[])
                .filter((portal: PortalInterface) => !portal.name.toLowerCase().includes('crm'))
                .map((item: PortalInterface) => new PortalModel(item));
        }

        return this.tradingPlatformService.getAll(variables.tenant).pipe(
            switchMap(this.mapPlatforms.bind(this, countries, portals)));
    }

    private mapPlatforms(
        countries: CountryModel[],
        portals: PortalModel[],
        response: ApiResponse<ApiError[] | TradingPlatformInfoInterface[]>
    ): Observable<ApiResponse<ApiError[] | ClientEntitiesReportResolverDataInterface>> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponseObservable(
                response,
                ClientEntitiesResolverErrorStatesEnum.GetTradingPlatformsFailed
            );
        }

        if (Helper.errorsHelper().checkForInvalidRespondedArray<TradingPlatformInfoInterface>(response)) {
            const errorResponse = Helper.errorsHelper().createResponseForInvalidData(
                ClientEntitiesResolverErrorStatesEnum.GetTradingPlatformsFailed
            );

            return of(errorResponse);
        }

        let tradingPlatforms = [];

        if (response.data !== null) {
            tradingPlatforms = (response.data as TradingPlatformInfoInterface[])
                .map((platformInterface: TradingPlatformInfoInterface) => {
                    return new TradingPlatformInfoModel(platformInterface);
                });
        }

        return of(Helper.enumsHelper().getClientStatuses())
            .pipe(
                switchMap(this.lookupMap.bind(this, countries, portals, tradingPlatforms))
            );
    }

    private lookupMap(
        countries: CountryModel[],
        portals: PortalModel[],
        tradingPlatforms: TradingPlatformInfoModel[],
        statuses: LookupModel
    ): Observable<ApiResponse<ApiError[] | ClientEntitiesReportResolverDataInterface>> {
        const statusCollection = statuses;
        const filterControls = this.mapFilterControls(countries, statusCollection, portals, tradingPlatforms);
        const filterCache = this.cacheCollectionService.getItem(this.componentStorageKeyProvider.getFilterModelKey())?.data;
        if (filterCache) {
            return this.reportsService.getIndividualsReport(filterCache)
                .pipe(
                    map(this.clientsReportMap.bind(
                        this,
                        countries,
                        statusCollection,
                        filterCache,
                        portals,
                        tradingPlatforms,
                        filterControls)),
                );
        }

        const itemsPerPagesChunk = Pagination.calculateItemsPerPagesChunk();

        const filter = new IndividualFilterModel(
            null,
            null,
            null,
            null,
            this.getDefaultIndividualStatuses(),
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            Pagination.defaultPagesChunk,
            itemsPerPagesChunk,
            IndividualReportModel.getEnrollDateSelector(),
            false,
            null,
            null
        );

        return this.reportsService.getIndividualsReport(filter)
            .pipe(
                map(this.clientsReportMap.bind(this,
                    countries,
                    statusCollection,
                    filter,
                    portals,
                    tradingPlatforms,
                    filterControls)),
            );
    }

    protected clientsReportMap(
        countries: CountryModel[],
        statusCollection: LookupModel,
        initialFilter: IndividualFilterModel,
        portals: PortalModel[],
        tradingPlatforms: TradingPlatformInfoModel[],
        filterControls: FilterControl[],
        response: ApiResponse<ApiError[] | IndividualReportInterface[]>

    ): ApiResponse<ApiError[] | ClientEntitiesReportResolverDataInterface> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse(
                response,
                ClientEntitiesResolverErrorStatesEnum.UserSearchFailed
            );
        }

        const data = response.data;

        let modelsCollection = [];

        if (data !== null) {
            modelsCollection = this.mapClientsReportData(
                data as IndividualReportInterface[],
                countries,
                statusCollection,
                portals,
                tradingPlatforms
            );
        }

        return new ApiResponse<ClientEntitiesReportResolverDataInterface>(HttpStatusCodesEnum.Ok, {
            filterControls,
            dataTableConfig: this.getDataTableConfig(),
            modelsCollection,
            countries,
            statusCollection,
            individualInitialFilter: initialFilter,
            portals,
            tradingPlatforms
        });
    }

    protected mapClientsReportData(
        data: IndividualReportInterface[],
        countries: CountryModel[],
        statusCollection: LookupModel,
        portals: PortalModel[],
        tradingPlatforms: TradingPlatformInfoModel[]
    ): IndividualReportModel[] {
        return data.map((report: IndividualReportInterface) => {
            const model = new IndividualReportModel(report);

            model.statusName = '';
            model.countryName = '';
            model.enrollDate = Helper.formatDate(model.enrollDate);
            model.verificationDate = Helper.formatDate(model.verificationDate);

            model.portalNames = portals
                .filter((portal: PortalModel) => model.portalIds?.length > 0 && model.portalIds.includes(portal.id))
                .map((portal: PortalModel) => portal.name)
                .join(', ');

            const filteredStatusKeys = statusCollection.getTypeKeys().filter((status: string) => {
                return parseInt(status, 10) === model.status;
            });

            model.statusGroup = EnumsHelper.getStatusGroupName(model.status);


            if (filteredStatusKeys.length !== 0) {
                model.statusName = statusCollection.getTypeValue(filteredStatusKeys[0]);
            }

            const filteredCountries = countries.filter((country: CountryModel) => {
                return country.id === model.countryId;
            });

            if (filteredCountries.length !== 0) {
                model.countryName = filteredCountries[0].name;
            }

            model.residentialCountryName = countries.find((country: CountryModel) => country.id === model.residentialCountryId)?.name;
            model.enrollTradingPlatformName = tradingPlatforms.find((platform: TradingPlatformInfoModel) =>
                platform.id === model.enrollTradingPlatformId)?.name;
            model.platformSubscriptionList = model.platformSubscriptions
                .map(sub => {
                    const platform = tradingPlatforms.find(p => p.id === sub.trading_platform_id);
                    if (!platform) { return null; }

                    if (sub.trading_platform_version_id && sub.trading_platform_version_id > 0) {
                        const version = platform.trading_platform_versions
                            .find(v => v.id === sub.trading_platform_version_id);

                        return version?.name ?? platform.name;
                    }

                    return platform.name;
                })
                .filter((name): name is string => Boolean(name)).join(', ');

            return model;
        });
    }

    protected abstract getDefaultIndividualStatuses(): number[];

    protected abstract mapFilterControls(
        countries: CountryModel[],
        statusCollection: LookupModel,
        portals: PortalModel[],
        tradingPlatforms: TradingPlatformInfoModel[]
    ): FilterControl[];

    protected abstract getDataTableConfig(): ReportTableConfigInterface;

    resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<ApiResponse<ApiError[] | ClientEntitiesReportResolverDataInterface>> {
        return this.countriesService.getAll()
            .pipe(
                switchMap(this.countriesMap.bind(this))
            );
    }
}
